import {useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import "./system-notification.css";
import useAppContext from "../../hooks/useAppContext";
import useUserContext from "../../hooks/useUserContext";
import {getShopTotalDuePayments} from "../../api/getShopTotalDuePayments";
import {getShopTotalFundsAdded} from "../../api/getShopTotalFundsAdded";
import TwilioOnBoarding from "../whatsappBusinessAccount/onBoardingProcess/TwilioOnBoarding";
import {getTwilioSender} from "../../api/getTwilioSender";
import useCredits from "../../hooks/useCredits";
import {Button} from "antd";
import WarningIcon from "../../assets/images/warning-icon.png";
import {CoinIcon} from "../../assets/icons/CoinIcon";
import CreditModal from "../paymentsSection/CreditModal";
import PricingPlanModal from "../paymentsSection/PricingPlanModal";
import useGlobalModal from "../../hooks/useGlobalModal";
import {FlowProps} from "../../types/FlowProps";
import {MessageTemplateProps} from "../../types/MessageTemplateProps";
import SystemBanner from "./SystemBanner";
import OnboardBannerBg from "../../assets/images/onboard-banner-background.png";
import WriteIcon from "../../assets/icons/WriteIcon";
import {BarcodeIcon} from "../../assets/icons/BarcodeIcon";
import V2CopilotPage from "../../pages/copilot/V2CopilotPage";
import useFlag from "../../hooks/useFlag";
import {isFreePricingPlanId} from "../../consts/freePricingPlans";
import { detectUILangSync } from "../../utils/detectUILang";

type UILang = "en" | "he";

function getUILang(): UILang {
    const stored = localStorage.getItem("v2_onboarding_lang") || localStorage.getItem("v2_vivo_lang");
    if (stored === "he" || stored === "en") return stored;
    return detectUILangSync();
}

const TR = {
    en: {
        onboarding_title: "Complete your onboarding to Vuelve",
        onboarding_subtitle: "Welcome to Vuelve! To start using your account, please complete the onboarding process. Click the button below to get started.",
        onboarding_cta: "Complete onboarding",
        coupon_title: "Coupon templates not able to be sent",
        coupon_subtitle: "Some templates can't be sent. They include a coupon code, but no products allow discounts. Set product discount rules in the Coupon Panel (accessible from the Automations page).",
        coupon_cta: "Set Coupon Rules",
        credits_low_title: (n: number) => `Running low on credits (Only ${n} left)`,
        credits_out_title: "You're out of credits",
        credits_low_subtitle: "Keep campaigns flowing — top up now or upgrade plan for better rates.",
        credits_out_subtitle: "Top up now or upgrade to keep carts & campaigns running.",
        buy_credits: "Buy credits",
        see_plans: "See plans",
        no_recovery_title: "No active recovery automations",
        no_recovery_subtitle: "Recovery is paused. Create an automation to start recovering carts.",
        create_automations: "Create automations",
        pending_title: "Some templates are pending approval",
        pending_subtitle: "A few templates are still awaiting WhatsApp's review, which can take up to 24 hours. Pending templates won't be active until approved.",
        view_automations: "View automations",
        rejected_title: "Some templates were rejected by WhatsApp",
        rejected_subtitle: "These templates can't be used until approved. Update and resubmit them to get approved.",
        review_automations: "Review automations",
        no_waba: "You haven't set a Whatsapp Business account yet. This is blocking you from sending messages to your clients Whatsapp accounts. Please click here to start onBoarding your business to the Whatsapp Business platform and integrate it with Vuelve",
        no_phone: "Your account doesn't have a phone number. This is blocking you from sending messages to your clients Whatsapp accounts. We will attach a phone number to your account in the coming 48 hours and will let you know once it's done. Click here for more details over the Whatsapp Business Account section of your settings page.",
        no_payment: "Please make sure you added a payment method to your Whatsapp Business Account. Missing a payment method on your Whatsapp Business Account will cause every message attempt to fail. Click here to learn more on the Whatsapp Business Account section on your settings page.",
        payment_success: "Your payment settings were saved successfully.",
        payment_fail: "We encountered some problems while saving your payment settings to our system. Please set your payment settings once again, to make sure you'll be able to send messages via Vuelve",
        no_spending_limit: "You need to set spending limit on your Vuelve App on Shopify. Click here to set your capped amount.",
        spending_limit_close: "Your used balance is getting close to your spending limit. If you won't increase your capped amount, you might not be able to send messages",
        spending_limit_reached: "You have no more funds to use. Please increase your capped amount to keep sending messages and recover abandoned carts",
        waba_unstable: "Your connection to Whatsapp appears to be unstable or not working correctly. This may affect your ability to send or receive messages. Please reconnect your Meta account to ensure uninterrupted service.",
    },
    he: {
        onboarding_title: "השלם את תהליך ההטמעה שלך ב-Vuelve",
        onboarding_subtitle: "ברוך הבא ל-Vuelve! כדי להתחיל להשתמש בחשבון שלך, אנא השלם את תהליך ההטמעה. לחץ על הכפתור למטה כדי להתחיל.",
        onboarding_cta: "השלם את תהליך ההטמעה",
        coupon_title: "לא ניתן לשלוח תבניות קופון",
        coupon_subtitle: "חלק מהתבניות לא יכולות להישלח. הן כוללות קוד קופון, אך אף מוצר לא מאפשר הנחות. הגדר כללי הנחה במוצרים דרך לוח הקופונים (נגיש מדף האוטומציות).",
        coupon_cta: "הגדר כללי קופון",
        credits_low_title: (n: number) => `נגמרים הקרדיטים (נותרו רק ${n})`,
        credits_out_title: "נגמרו הקרדיטים שלך",
        credits_low_subtitle: "שמור על זרימת הקמפיינים — טען עכשיו או שדרג תכנית לתעריפים טובים יותר.",
        credits_out_subtitle: "טען עכשיו או שדרג כדי לשמור על פעילות העגלות והקמפיינים.",
        buy_credits: "רכוש קרדיטים",
        see_plans: "ראה תכניות",
        no_recovery_title: "אין אוטומציות שחזור פעילות",
        no_recovery_subtitle: "שחזור העגלות מושהה. צור אוטומציה כדי להתחיל לשחזר עגלות.",
        create_automations: "צור אוטומציות",
        pending_title: "חלק מהתבניות ממתינות לאישור",
        pending_subtitle: "מספר תבניות עדיין ממתינות לבדיקת WhatsApp, מה שיכול לקחת עד 24 שעות. תבניות ממתינות לא יהיו פעילות עד לאישור.",
        view_automations: "צפה באוטומציות",
        rejected_title: "חלק מהתבניות נדחו על ידי WhatsApp",
        rejected_subtitle: "תבניות אלו לא ניתנות לשימוש עד לאישור. עדכן והגש אותן מחדש לקבלת אישור.",
        review_automations: "בדוק אוטומציות",
        no_waba: "עדיין לא הגדרת חשבון WhatsApp Business. זה מונע ממך לשלוח הודעות ללקוחות שלך. לחץ כאן כדי להתחיל בתהליך ההטמעה.",
        no_phone: "לחשבון שלך אין מספר טלפון. זה מונע ממך לשלוח הודעות. נצרף מספר טלפון לחשבונך תוך 48 שעות ונעדכן אותך. לחץ כאן לפרטים.",
        no_payment: "אנא ודא שהוספת אמצעי תשלום לחשבון WhatsApp Business שלך. ללא אמצעי תשלום, כל ניסיון לשלוח הודעה ייכשל.",
        payment_success: "הגדרות התשלום שלך נשמרו בהצלחה.",
        payment_fail: "נתקלנו בבעיות בשמירת הגדרות התשלום שלך. אנא הגדר את הגדרות התשלום שוב כדי לוודא שתוכל לשלוח הודעות דרך Vuelve.",
        no_spending_limit: "עליך להגדיר מגבלת הוצאה לאפליקציית Vuelve ב-Shopify. לחץ כאן להגדרת הסכום המוגבל.",
        spending_limit_close: "היתרה שבשימוש מתקרבת למגבלת ההוצאה שלך. אם לא תגדיל את הסכום המוגבל, ייתכן שלא תוכל לשלוח הודעות.",
        spending_limit_reached: "אין לך יותר כספים לשימוש. אנא הגדל את הסכום המוגבל שלך כדי להמשיך לשלוח הודעות ולשחזר עגלות נטושות.",
        waba_unstable: "החיבור שלך ל-WhatsApp נראה לא יציב או לא פועל כראוי. זה עלול להשפיע על יכולתך לשלוח או לקבל הודעות. אנא התחבר מחדש לחשבון Meta שלך.",
    },
} as const;

enum SYSTEM_NOTIFICATION_TYPES {
    URGENT = 'urgent',
    WARNING = 'warning',
    SUCCESS = 'success',
}

interface SystemNotificationItemProps {
    text: string;
    type: SYSTEM_NOTIFICATION_TYPES,
    path?: string;
}

const SystemNotification = () => {
    const { data: { waba, shop, shopifyBillingDetails, flows, templates, discounts } } = useAppContext();
    const { selectedDomain } = useUserContext();
    const lang = getUILang();
    const tr = TR[lang];
    const isRtl = lang === "he";
    const [notifications, setNotifications] = useState<SystemNotificationItemProps[]>([]);
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const [isOnBoarding, setIsOnboarding] = useState(false);
    const [isOnBoardingDone, setIsOnBoardingDone] = useState(false);
    const paymentUpdateStatus = new URLSearchParams(search).get("payment_update_status");
    const cap = new URLSearchParams(search).get("cap");
    const isShopify = shop.data.platformId === 1;
    const shopifyBalanceUsed = isShopify && shopifyBillingDetails.isLoaded ? parseInt(shopifyBillingDetails?.data?.recurring_application_charge?.balance_used || 0) : null;
    const shopifyCappedAmount = isShopify && shopifyBillingDetails.isLoaded ? parseInt(shopifyBillingDetails?.data?.recurring_application_charge?.capped_amount || 0) : null;
    const [whatsappAccountState, setWhatsappAccountState] = useState<{
        title?: string;
        description?: string;
        messagesLimit?: number;
    } | null>(null);
    const [whatsappAccountUpdatedAt, setWhatsappAccountUpdatedAt] = useState(new Date());
    const phone = useMemo(() => waba?.data?.waba?.phones?.[0], [waba]);
    const wabaId = useMemo(() => waba?.data?.waba?.id, [waba]);
    const { isPlanSupportCredits, allowedToPurchaseCredits, availableCredits } = useCredits();
    const shouldShowCreditsAd = availableCredits < 15 && allowedToPurchaseCredits && isPlanSupportCredits;
    const [hasActiveRecoveryTemplates, setHasActiveRecoveryTemplates] = useState(true);
    const [hasPendingTemplates, setHasPendingTemplates] = useState(false);
    const [hasRejectedTemplates, setHasRejectedTemplates] = useState(false);

    const shouldShowRecoveryAutomationAd = !hasActiveRecoveryTemplates;
    const shouldShowPendingAd = hasPendingTemplates;
    const shouldShowRejectedAd = hasRejectedTemplates && !hasPendingTemplates;
    const { setIsCreditModalOpen, setIsPricingPlanModalOpen } = useGlobalModal();
    const hasTemplatesWithCouponCode = !!flows.data?.find((flow: FlowProps) => flow.active && !!flow.templates?.find(template => template.content.includes('{{coupon code}}') && template.active && template.whatsappTemplateStatus && ['APPROVED','PENDING'].includes(template.whatsappTemplateStatus)));
    const shouldShowNoDiscountsForCouponCodeAlert = templates.isLoaded && hasTemplatesWithCouponCode && discounts.isLoaded && discounts.data.length < 5;
    const { isFlagOn } = useFlag();
    const isV2Enabled = isFlagOn('v2');

    const loadNotifications = async () => {
        const newNotifications = [];
        const isWabaLoaded = waba?.isLoaded && !waba?.isLoading;
        const wabaId = waba?.data?.waba?.whatsappBusinessAccountId;
        const phone = waba?.data?.waba?.phones?.[0];
        const phoneId = phone?.id;
        const providerSenderId = waba?.data?.waba?.providerSenderId;
        const providerPhoneId = phone?.providerPhoneId;
        const phoneNumber = phone?.phoneNumber;
        const hasWaba = !!wabaId;
        const hasPhone = !!phoneId && !!phoneNumber;
        const hasPayment = true;
        const isShopify = shop.data.platformId === 1;

        // const duePayments = await loadDuePayments();
        // const totalPayments = await loadTotalFundsAdded();
        if(isWabaLoaded && (!hasPhone || !hasWaba) && !isOnBoardingDone) {
            setIsOnboarding(true);
            return [];
        } else if(hasWaba && hasPhone && isOnBoardingDone) {
            setIsOnboarding(false);
        }

        if(isWabaLoaded && !hasWaba && pathname !== '/settings' && pathname !== '/docs') {
            newNotifications.push({
                text: tr.no_waba,
                type: SYSTEM_NOTIFICATION_TYPES.URGENT,
                path: '/settings?section=meta',
            })
        }

        if(isWabaLoaded && !hasPhone && pathname !== '/settings') {
            newNotifications.push({
                text: tr.no_phone,
                type: SYSTEM_NOTIFICATION_TYPES.URGENT,
                path: '/settings?section=meta',
            })
        }

        if(isWabaLoaded && hasWaba && hasPhone && !hasPayment && pathname !== '/settings') {
            newNotifications.push({
                text: tr.no_payment,
                type: SYSTEM_NOTIFICATION_TYPES.WARNING,
                path: '/settings?section=meta',
            })
        }

        // if(!isShopify && duePayments > totalPayments && pathname !== '/settings') {
        //     newNotifications.push({
        //         text: "It seems like there aren't enough funds in your account. Please click here to add more funds, so your account will remain active.",
        //         type: SYSTEM_NOTIFICATION_TYPES.URGENT,
        //         path: '/settings?section=payments',
        //     });
        // } else if(!isShopify && duePayments + 50 > totalPayments && pathname !== '/settings') {
        //     newNotifications.push({
        //         text: "Your account is running out of funds. Please check your account's settings page to add more funds so your account will remain active",
        //         type: SYSTEM_NOTIFICATION_TYPES.WARNING,
        //         path: '/settings?section=payments',
        //     });
        // }

        if(shop.data.platformId === 1 && pathname === '/settings' && paymentUpdateStatus && cap) {
            if(paymentUpdateStatus === 'success') {
                newNotifications.push({
                    text: tr.payment_success,
                    type: SYSTEM_NOTIFICATION_TYPES.SUCCESS
                });
            } else {
                newNotifications.push({
                    text: tr.payment_fail,
                    type: SYSTEM_NOTIFICATION_TYPES.URGENT
                });
            }
        }

        if(isShopify && shopifyBillingDetails.isLoaded && pathname !== '/settings' && !isFreePricingPlanId(shop?.data?.pricingPlan?.id)) {
            if(!shopifyCappedAmount) {
                newNotifications.push({
                    text: tr.no_spending_limit,
                    type: SYSTEM_NOTIFICATION_TYPES.URGENT,
                    path: '/settings?section=payments',
                });
            } else if(shopifyCappedAmount > shopifyBalanceUsed! && shopifyCappedAmount * 0.85 <= shopifyBalanceUsed!) {
                newNotifications.push({
                    text: tr.spending_limit_close,
                    type: SYSTEM_NOTIFICATION_TYPES.WARNING,
                    path: '/settings?section=payments',
                });
            } else if(shopifyCappedAmount <= shopifyBalanceUsed!) {
                newNotifications.push({
                    text: tr.spending_limit_reached,
                    type: SYSTEM_NOTIFICATION_TYPES.URGENT,
                    path: '/settings?section=payments',
                });
            }
        }

        const now = new Date();
        const diffMs = now.getTime() - whatsappAccountUpdatedAt.getTime();

        if(diffMs > 1 * 60 * 1000 && whatsappAccountState?.title && whatsappAccountState.title !== 'online') {
            newNotifications.push({
                text: tr.waba_unstable,
                type: SYSTEM_NOTIFICATION_TYPES.URGENT,
                path: '/settings?section=account',
            });
        }

        setNotifications(newNotifications);
    }

    useEffect(() => {
        waba?.data && selectedDomain && shop.data?.id && loadNotifications();
    }, [waba?.data, pathname, selectedDomain, shop.data, shopifyBillingDetails.data, whatsappAccountState]);

    useEffect(() => {
        isOnBoarding && setIsOnboarding(false);
    }, [selectedDomain]);

    useEffect(() => {
        loadWabaStatus();
    }, [selectedDomain, phone, wabaId]);

    useEffect(() => {
        if(flows?.data?.length) {
            const achieved = {
                activeRecovery: false,
                pendingTemplates: false,
                rejectedTemplates: false,
            }
            for (let flow of flows?.data) {
                const nextHasActiveRecoveryTemplate = !!(flow?.flowTriggerId === 1 && flow?.active && !!flow?.templates?.find((template: MessageTemplateProps) => template.active));

                const nextHasPendingTemplates = flow?.active && !!flow?.templates?.find((template: MessageTemplateProps) => {
                    return template.whatsappTemplateStatus === 'PENDING';
                });

                const nextHasRejectedTemplates = !!(flow?.flowTriggerId === 1 && flow?.active && !!flow?.templates?.find((template: MessageTemplateProps) => template.whatsappTemplateStatus === 'REJECTED'));
                if(nextHasActiveRecoveryTemplate && !achieved.activeRecovery) {
                    achieved.activeRecovery = true;
                    setHasActiveRecoveryTemplates(nextHasActiveRecoveryTemplate);
                }
                if(nextHasPendingTemplates && !achieved.pendingTemplates) {
                    achieved.pendingTemplates = true;
                    setHasPendingTemplates(nextHasPendingTemplates);
                }
                if(nextHasRejectedTemplates && !achieved.rejectedTemplates) {
                    achieved.rejectedTemplates = true;
                    setHasRejectedTemplates(nextHasRejectedTemplates);
                }

                if(achieved.activeRecovery && achieved.pendingTemplates && achieved.rejectedTemplates) break;
            }
        }
    }, [flows.isLoaded, flows.data?.templates]);

    const loadWabaStatus = async () => {
        if(selectedDomain && phone && wabaId) {
            const response = await getTwilioSender(selectedDomain);
            if(response.data.createdAt) {
                setWhatsappAccountUpdatedAt(new Date(response.data.createdAt));
            }
            if(response.data.data) {
                setWhatsappAccountState({
                    title: response.data.data.status.toLowerCase(),
                    description: response.data.data?.offline_reasons?.[0]?.message,
                    messagesLimit: response.data.data?.properties?.messaging_limit
                })
            }
        }
    }

    const loadDuePayments = async () => {
        const response = await getShopTotalDuePayments(selectedDomain!);
        return parseFloat(response.data.duePayments);
    }

    const loadTotalFundsAdded = async () => {
        const response = await getShopTotalFundsAdded(selectedDomain!);
        return parseFloat(response.data.total);
    }

    const goTo = (path: string = '') => () => path && navigate(path);
    if(isOnBoarding && pathname !== '/settings' && pathname !== '/vivo') {
        return <SystemBanner
            rtl={isRtl}
            bannerImage={OnboardBannerBg}
            title={tr.onboarding_title}
            subtitle={tr.onboarding_subtitle}
            actions={<>
                <Button type="primary" size="large" onClick={() => navigate('/vivo')}>{tr.onboarding_cta}</Button>
            </>}
        />;
    }

    if(shouldShowNoDiscountsForCouponCodeAlert) {
        return <SystemBanner
                rtl={isRtl}
            title={tr.coupon_title}
            subtitle={tr.coupon_subtitle}
            actions={<>
                <Button type="primary" size="large" onClick={() => navigate('/automations', {
                    state: { openCouponPanel: true },
                })}><BarcodeIcon width={20} color="#fff" /><span>{tr.coupon_cta}</span></Button>
            </>}
        />;
    }

    if(shouldShowCreditsAd) {
        return <SystemBanner
                rtl={isRtl}
            title={availableCredits ? tr.credits_low_title(availableCredits) : tr.credits_out_title}
            subtitle={availableCredits ? tr.credits_low_subtitle : tr.credits_out_subtitle}
            actions={<>
                <Button type="primary" size="large" onClick={() => setIsCreditModalOpen(true)}>
                    <CoinIcon width={20} color="#fff" /><span>{tr.buy_credits}</span></Button>
                <Button type="default" onClick={() => setIsPricingPlanModalOpen(true)}>{tr.see_plans}</Button>
            </>}
        />;
    }

    if(shouldShowRecoveryAutomationAd) {
        return <SystemBanner
                rtl={isRtl}
            title={tr.no_recovery_title}
            subtitle={tr.no_recovery_subtitle}
            actions={<>
                {pathname.includes('/automation') || pathname.includes('/templates') ? null : <Button type="primary" size="large" onClick={() => navigate('/automations')}>
                    <WriteIcon width={20} color="#fff" /><span>{tr.create_automations}</span></Button>}
            </>}
        />;
    }

    if(shouldShowPendingAd) {
        return <SystemBanner
                rtl={isRtl}
            title={tr.pending_title}
            subtitle={tr.pending_subtitle}
            actions={<>
                {pathname.includes('/automation') || pathname.includes('/templates') ? null : <Button type="primary" size="large" onClick={() => navigate('/automations')}>
                    <span>{tr.view_automations}</span></Button>
                }
            </>}
        />;
    }

    if(shouldShowRejectedAd) {
        return <SystemBanner
                rtl={isRtl}
            title={tr.rejected_title}
            subtitle={tr.rejected_subtitle}
            actions={<>
                {pathname.includes('/automation') || pathname.includes('/templates') ? null : <Button type="primary" size="large" onClick={() => navigate('/automations')}>
                    <span>{tr.review_automations}</span></Button>
                }
            </>}
        />;
    }

    if(!notifications.length) return null;

    return <div className="system-notification-container">
        {notifications.map(notification => {
            return (
                <div key={`system-notification-${notification.text}`} className={`system-notification ${notification.type}`} onClick={goTo(notification.path)}>
                    {notification.text}
                </div>
            );
        })}
    </div>
}

export default SystemNotification;